// The filters each role's grid opens with.
//
// Kept beside the filter engine rather than in the page: it is pure logic with no
// rendering in it, and living here means it can be unit-tested without pulling a
// page (and MSAL, and window) into the test run.
import type { LoanPhase } from '@app/shared';
import { col } from './columns';
import { newFilterKey, type Filter } from './filterModel';

// Assignment is expressed as an ordinary filter rather than a checkbox or a
// server-side scope, so every view can open on "my work" (#3) and still be
// re-pointed at a colleague's queue — or at unassigned loans — by editing the
// chip. Analyst wins when a user holds both roles; admins get no assignment
// scope, since overseeing the whole portfolio is the job.
//
// Everyone also opens with Prepurchase hidden. It's a backstop: a loan still in
// Prepurchase currently keeps its assignment, so it surfaces in "my work" while
// there is nothing yet to work. A planned workflow change drops the assignment
// at that stage, after which these loans fall out on their own and this chip
// becomes redundant — hence a removable default rather than a hard rule.
const PREPURCHASE: LoanPhase = 'Prepurchase';

export function defaultFiltersFor(
  me: { oid: string; displayName: string },
  isAdmin: boolean, isAnalyst: boolean, isSa: boolean,
): Filter[] {
  // "Mine" means mine OR nobody's (#4). A loan waiting to be picked up has no
  // assignee, so scoping strictly to yourself hides exactly the work you're meant
  // to claim. The Unassigned bucket's value is the empty string, and `equals` on an
  // enum is any-of, so both live in one chip the user can still edit.
  //
  // Labels are supplied up front rather than looked up, so a chip reads properly
  // even when nothing currently loaded carries the value it matches on.
  const mine = (columnId: string): Filter[] => [{
    key: newFilterKey(),
    columnId,
    op: 'equals',
    values: [me.oid, ''],
    labels: { [me.oid]: me.displayName, '': col(columnId).emptyLabel ?? 'Unassigned' },
  }];

  const notPrepurchase: Filter = {
    key: newFilterKey(),
    columnId: 'phase',
    op: 'notEquals',
    values: [PREPURCHASE],
    labels: { [PREPURCHASE]: PREPURCHASE },
  };

  const assignment = isAdmin ? []
    : isAnalyst ? mine('assignedLoanAnalystName')
    : isSa ? mine('assignedSaOfficerName')
    : [];

  return [...assignment, notPrepurchase];
}
